import { useCallback, useRef } from 'react';
import { NativeModules } from 'react-native';
import { isWeb } from '../utils';

/**
 * - RN의 스크롤 이벤트를 Native로 전달하여 AppBar, Top 버튼 등을 제어합니다.
 * - scrollThreshold 이상 스크롤되면 Top 버튼이 표시됩니다. (기본값: 200)
 * - scrollDirection은 손가락을 뗀 시점(onScrollEndDrag)에만 갱신되어 AppBar 떨림이 없습니다.
 */
export const useNativeScrollSync = ({
  instanceId,
  scrollThreshold = 200,
}: {
  instanceId: string;
  scrollThreshold?: number;
}) => {
  const lastDirection = useRef<'up' | 'down'>('up');
  const lastIsFromTop = useRef(false);
  const lastScrollY = useRef(0);
  const scrollYAtDragStart = useRef(0);

  const onScroll = useCallback(
    (scrollY: number) => {
      if (isWeb) {
        return;
      }
      lastScrollY.current = scrollY;
      const isScrolledFromTop = scrollY > scrollThreshold;
      if (isScrolledFromTop !== lastIsFromTop.current) {
        NativeModules.ReactNativeBridge.onScrollEvent({
          instanceId,
          isScrolledFromTop,
          scrollDirection: lastDirection.current,
        });
        lastIsFromTop.current = isScrolledFromTop;
      }
    },
    [instanceId, scrollThreshold],
  );

  /** 드래그 시작 시점의 scrollY 저장. ScrollView onScrollBeginDrag에서 contentOffset.y 전달 권장 */
  const onScrollBeginDrag = useCallback((scrollY?: number) => {
    if (isWeb) {
      return;
    }
    scrollYAtDragStart.current = scrollY ?? lastScrollY.current;
  }, []);

  /** 손가락 뗀 시점에 방향 결정 후 한 번만 전달 */
  const onScrollEndDrag = useCallback(
    (scrollY: number) => {
      if (isWeb) {
        return;
      }
      const start = scrollYAtDragStart.current;
      const direction =
        scrollY <= 0 ? 'up' : scrollY > start ? 'down' : 'up';
      const isScrolledFromTop = scrollY > scrollThreshold;

      if (
        direction !== lastDirection.current ||
        isScrolledFromTop !== lastIsFromTop.current
      ) {
        NativeModules.ReactNativeBridge.onScrollEvent({
          instanceId,
          isScrolledFromTop,
          scrollDirection: direction,
        });
        lastDirection.current = direction;
        lastIsFromTop.current = isScrolledFromTop;
      }
      lastScrollY.current = scrollY;
    },
    [instanceId, scrollThreshold],
  );

  return { onScroll, onScrollBeginDrag, onScrollEndDrag };
};
